import { gql } from "@apollo/client";

export const GET_LIST_EVENTS = gql`
  query events($where: events_bool_exp!) {
    events(where: $where, order_by: { name: asc }) {
      id
      end_date
      currency
      created_at
      configuration
      name
      description
      logo
      background_url
      payment_types
      color_mode
      dynamic_descriptor
      digital_surcharge_label
      payment_processor_id
      ticketing_logo
      ticketing_background_url
      ticketing_payment_processor_id
      invoicing_logo
      invoicing_background_url
      invoicing_payment_processor_id
      uid_reg_flow
      cashless_top_up_sales
      open_loop_cashless
      payment_processor_config {
        id
        payment_processor
        processing_rate
        config
      }

      organization {
        name
      }
      organization_id
      slug

      start_date
      timezone
      timezone_diff
      updated_at
      locations {
        name
        id
      }
      is_active
      sub_events
      user {
        username
      }
    }
  }
`;

// ---------------------------------------------------------------------------
// Merch Manager per-artist deals (Inventory Configuration tab).
//
// Three tables owned by Merch Manager that admin-app otherwise never touches:
//
//   merch_vendor_deal       One row per (event_id, vendor_id) holding the
//                           revenue split for that ARTIST at that event.
//                           NO ROW = the legacy two-party model (the festival's
//                           cut comes from vendors.revenue_share), so never
//                           insert a placeholder row "just to have one".
//   vendor_settlement       One row per (event_id, vendor_id) — the artist's
//                           settlement. `locked_at` non-null means the numbers
//                           are signed: the split AND the fee lines are frozen.
//   vendor_settlement_line  The fee / deduction rows that hang off a settlement
//                           (settlement_id FK). These are pre-event CONFIG, not
//                           settlement-time additions.
//
// Every one of these is queried in its own document rather than joined into
// GET_EVENT so that a missing Hasura permission on the merch tables degrades to
// "couldn't load the deals" instead of breaking the whole event edit screen.
// ---------------------------------------------------------------------------

// Vendors are NOT event-scoped (no events↔vendors join table) — an event's
// artists are derived from the active locations placed at that event. The same
// vendor can own several locations, so de-dupe by vendor id client-side.
export const GET_EVENT_MERCH_ARTISTS = gql`
  query EventMerchArtists($eventId: Int!) {
    locations(where: { event_id: { _eq: $eventId }, is_active: { _eq: true } }) {
      vendor_id
      vendor {
        id
        name
        type
      }
    }
  }
`;

// Every deal + settlement (with its fee lines) for the event in one round trip.
// `lines` is the vendor_settlement → vendor_settlement_line array relationship.
export const GET_EVENT_MERCH_DEALS = gql`
  query EventMerchDeals($eventId: Int!) {
    merch_vendor_deal(where: { event_id: { _eq: $eventId } }) {
      id
      vendor_id
      split_mode
      artist_pct
      organizer_pct
      manager_pct
      manager_vendor_id
      manager_paid_in_app
      is_active
    }
    vendor_settlement(where: { event_id: { _eq: $eventId } }) {
      id
      vendor_id
      status
      locked_at
      lines {
        id
        type
        label
        flat_or_pct
        rate
        amount_cents
        off_top
        kept_by
      }
    }
  }
`;

// The vendor managers assigned TO THIS EVENT (migration 008).
//
// merch_event_manager is the (event_id, vendor_id) join table that scopes the
// manager picker: a festival's org can carry dozens of vendor managers and only
// a handful work any given event. `vendor` is the object relationship back to
// vendors — is_merch_vendor_manager is NULLABLE, and rows assigned by hand
// before 008 landed may have it null, so DON'T filter the picker on it. Being
// in this table is the assignment.
//
// This query FAILS OUTRIGHT (validation error, not an empty list) in any
// environment where 008 hasn't been applied and the table isn't tracked in
// Hasura — callers must fall back to GET_MERCH_DEAL_MANAGER_CANDIDATES.
export const GET_EVENT_MERCH_MANAGERS = gql`
  query EventMerchManagers($eventId: Int!) {
    merch_event_manager(
      where: { event_id: { _eq: $eventId }, is_active: { _eq: true } }
    ) {
      vendor_id
      vendor {
        id
        name
        is_merch_vendor_manager
      }
    }
  }
`;

// Candidates for merch_vendor_deal.manager_vendor_id — the vendor manager's OWN
// vendor row (that's where their Stripe connected account hangs). Any active
// vendor in the event's organization except the artist themselves.
//
// Two jobs now: the fallback for GET_EVENT_MERCH_MANAGERS above, and the source
// list for "Add existing vendor" (org vendors that aren't at this event yet).
export const GET_MERCH_DEAL_MANAGER_CANDIDATES = gql`
  query MerchDealManagerCandidates($organizationId: Int!) {
    vendors(
      where: {
        organization_id: { _eq: $organizationId }
        is_active: { _eq: true }
      }
      order_by: { name: asc }
    ) {
      id
      name
    }
  }
`;

// One artist's physical setup at this event: their locations and the menus hanging
// off them.
//
//   locations     carry BOTH event_id and vendor_id — this row is what puts the
//                 vendor on the event (GET_EVENT_MERCH_ARTISTS derives the list
//                 above from it), so deactivating an artist's LAST one takes
//                 them off this tab entirely. That is allowed (see
//                 DEACTIVATE_MERCH_LOCATION) but only behind a confirmation
//                 that says so in as many words.
//   menus         also (event_id, vendor_id). A vendor can own several.
//   location_menu the JOIN (SINGULAR table name). One menu can serve MANY
//                 locations AND a location can carry MORE THAN ONE menu (a
//                 shared location with a food menu and a merch menu). Callers must
//                 therefore attach/detach individual join ROWS by their own id
//                 — never "set the menus on this location", which would silently
//                 detach another module's menu.
//
// `location_menus` is the array relationship from locations onto that join
// table, and `menu` the object relationship back to menus — the same traversal
// modules/locations/queries.ts uses. Because it exists, no second round trip
// against location_menu is needed here.
//
// Merch Manager's item builder writes new items into the menu it finds for a
// (location, vendor) pair, so which menu is attached decides where items land.
export const GET_MERCH_VENDOR_SETUP = gql`
  query MerchVendorSetup($eventId: Int!, $vendorId: Int!) {
    locations(
      where: {
        event_id: { _eq: $eventId }
        vendor_id: { _eq: $vendorId }
        is_active: { _eq: true }
      }
      order_by: { name: asc }
    ) {
      id
      name
      location_type
      location_menus {
        id
        menu_id
        menu {
          id
          name
        }
      }
    }
    menus(
      where: {
        event_id: { _eq: $eventId }
        vendor_id: { _eq: $vendorId }
        is_active: { _eq: true }
      }
      order_by: { name: asc }
    ) {
      id
      name
    }
  }
`;

// Every active location at this event, with whoever currently owns it.
//
// Feeds "Add location → Existing location": a location that is already at this
// event can be handed to an artist instead of minting a second one next to it.
//
// `vendor` is selected because locations.vendor_id is OWNERSHIP, not a tag.
// Pointing it at a different vendor MOVES the location — and Merch Manager's
// counts (inventory_count_line) hang off location_id, so whatever was counted
// there goes with it. The picker must therefore name the current owner, and the
// caller must confirm before taking a location off somebody else.
export const GET_EVENT_LOCATION_POOL = gql`
  query EventLocationPool($eventId: Int!) {
    locations(
      where: { event_id: { _eq: $eventId }, is_active: { _eq: true } }
      order_by: { name: asc }
    ) {
      id
      name
      vendor_id
      vendor {
        id
        name
      }
    }
  }
`;

// What would be hidden by deactivating one location.
//
// Merch Manager's physical counts (inventory_count_line) hang off location_id.
// Deactivating the location does NOT delete them — they stay in the database
// and on any settlement already computed from them — but they stop being
// reachable from either UI, so the confirmation has to say how many there are
// BEFORE the write, not after.
//
// This query can fail outright (not return zero) in an environment where
// inventory_count_line isn't tracked in Hasura. Callers must treat that as
// "unknown" and say so, NOT as "none" — and must not block the removal on it.
export const GET_MERCH_LOCATION_USAGE = gql`
  query MerchLocationUsage($locationId: Int!) {
    inventory_count_line_aggregate(
      where: { location_id: { _eq: $locationId } }
    ) {
      aggregate {
        count
      }
    }
    orders_aggregate(where: { location_id: { _eq: $locationId } }) {
      aggregate {
        count
      }
    }
  }
`;

// The org's item-category catalogue — the SAME source Inventory Manager reads
// (ItemsService.GetItemCategoriesAsync) and the same one the Items screen's
// dropdown uses. `reference_value` is what items.category stores; the label is
// display only. organization_id 0 = Ronin's shared/global references, which is
// why this is an _in and not an _eq.
export const GET_ORG_ITEM_CATEGORIES = gql`
  query OrgItemCategories($organizationId: Int!) {
    dashboard_reference(
      where: {
        organization_id: { _in: [0, $organizationId] }
        is_active: { _eq: true }
        reference_type: { _eq: "item_category" }
      }
      order_by: { reference_label: asc }
    ) {
      reference_value
      reference_label
    }
  }
`;

// Just the configuration blob, for the blocks in the Inventory Configuration
// tab that read and write it themselves rather than going through the event
// form (the form can only carry scalars through `name` attributes).
export const GET_EVENT_MERCH_CONFIG = gql`
  query EventMerchConfig($eventId: Int!) {
    events_by_pk(id: $eventId) {
      id
      configuration
    }
  }
`;

// Everything that makes removing an ARTIST from this event unsafe.
//
// Removing an artist means deactivating every active location they own here —
// an artist is on this tab BECAUSE of those locations (see
// GET_EVENT_MERCH_ARTISTS), there is no events↔vendors join to unlink.
//
// Counts are keyed by (event, vendor) at the session level, so they are found
// even if the locations have since been renamed or reassigned. Sales are keyed
// by location, so the caller passes the ids it is about to deactivate — an
// empty list must be sent as [-1] rather than [], because `location_id: {_in:
// []}` matches NOTHING and would report a reassuring zero.
export const GET_MERCH_ARTIST_USAGE = gql`
  query MerchArtistUsage($eventId: Int!, $vendorId: Int!, $locationIds: [Int!]!) {
    inventory_count_session_aggregate(
      where: { event_id: { _eq: $eventId }, vendor_id: { _eq: $vendorId } }
    ) {
      aggregate {
        count
      }
    }
    inventory_count_line_aggregate(
      where: { location_id: { _in: $locationIds } }
    ) {
      aggregate {
        count
      }
    }
    orders_aggregate(where: { location_id: { _in: $locationIds } }) {
      aggregate {
        count
      }
    }
    # A settlement row means fees were configured or a settlement was started.
    # status/locked_at distinguish "someone opened it" from "this is signed".
    vendor_settlement(
      where: { event_id: { _eq: $eventId }, vendor_id: { _eq: $vendorId } }
    ) {
      id
      status
      locked_at
    }
  }
`;

export const GET_EVENT = gql`
  query EventByPk($id: Int!) {
    events_by_pk(id: $id) {
      id
      configuration
      name
      start_date
      end_date
      currency
      description
      logo
      background_url
      payment_types
      payment_processor_id
      signup_questions
      frontgate_site_id
      dynamic_descriptor
      location_zones
      available_tokens
      payment_processor_config {
        payment_processor
      }

      organization_id
      slug
      color_mode
      digital_surcharge_label
      timezone
      timezone_diff
      is_active
      is_org_logout_protected
      is_clerk_logout_protected
      event_passcode
      web_surcharge_percentage
      start_time
      end_time
      address_street
      address_street_2
      address_city
      address_state
      address_zipcode
      ticketing_slug
      ticketing_logo
      ticketing_background_url
      ticketing_payment_processor_id
      ticketing_service_fee_per_order
      invoicing_logo
      invoicing_background_url
      invoicing_payment_processor_id
      require_cash_loading_customer_fields
      uid_reg_flow
      cashless_top_up_sales
      open_loop_cashless
      is_asset_transfer_combine
      event_logo_base64
      top_up_service_fee_per_order
      top_up_service_fee_percentage
      ticketing_configurations
      cashless_configurations
      web_checkout_configurations
      sub_events
    }
  }
`;
